import React from "react";
import PhoneInput from "react-phone-input-2";
import { FormControl, FormHelperText, MenuItem, Select } from "@mui/material";
import BaseTextField from "../../../../components/ui/BaseTextField";
import SearchLocation from "../../../../components/SearchLocation/SearchLocation";
import FileAttachmentField from "./components/FileAttachmentField";

const cell = "lg:mr-10 lg:col-span-1 col-span-2";

// Every company field, defined once. The profile wizard lays them out in its grid and
// the settings page puts one section's worth in a modal; neither redefines a field.
const companyFormFields = (formik, { disabled = false, isFreelanceCompany = false } = {}) => {
  const error = (name) => formik.touched[name] && formik.errors[name];

  const required = (label) => (
    <span>
      {label} <span className="text-red-500">*</span>
    </span>
  );

  const text = (name, label, { optional = false } = {}) => (
    <div className={cell}>
      <BaseTextField
        fullHeight={false}
        fullWidth
        labelShrink={false}
        id={name}
        name={name}
        label={label}
        optional={optional}
        value={formik.values?.[name]}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={error(name)}
        helperText={error(name)}
        disabled={disabled}
      />
    </div>
  );

  const file = (name, label, { optional = false } = {}) => (
    <FileAttachmentField
      label={label}
      name={name}
      formik={formik}
      optional={optional}
      disabled={disabled}
    />
  );

  const handlePlaceSelected = (location) => {
    formik.setFieldValue("address", location.completeAddress || "");
    formik.setFieldValue("country", location.country || "");
    formik.setFieldValue("city", location.city || "");
    formik.setFieldValue("state", location.state || "");
    formik.setFieldValue("zipCode", location.zipCode || "");
    formik.setFieldValue("latitude", location.latitude || "");
    formik.setFieldValue("longitude", location.longitude || "");
  };

  return {
    name: text("name", "Name"),
    email: text("email", "Email", { optional: true }),
    website: text("website", "Website", { optional: true }),
    fax: text("fax", "Fax"),

    phone: (
      <div className={cell}>
        <label htmlFor="mobileNumber" className="block text-sm font-sm mb-1 text-[#505050]">
          Phone Number <span className="font-light italic">- Optional</span>
        </label>
        <div className="relative w-full">
          <PhoneInput
            inputClass={`!w-full !h-[42px] !pl-12 !pr-3 !text-sm !font-light !border !rounded-md ${
              disabled ? "!bg-gray-[#F6F6F6] !cursor-not-allowed" : "!bg-white"
            }`}
            buttonClass={`!border-none !bg-transparent ${disabled ? "!cursor-not-allowed" : ""}`}
            specialLabel=""
            country="us"
            value={formik.values.mobileNumber}
            onChange={(value) => !disabled && formik.setFieldValue("mobileNumber", value)}
            inputProps={{
              name: "mobileNumber",
              id: "mobileNumber",
              type: "tel",
              disabled,
              style: {
                backgroundColor: disabled ? "#f3f4f6" : "#ffffff",
                cursor: disabled ? "not-allowed" : "text",
              },
            }}
            onBlur={() => formik.setFieldTouched("mobileNumber", true)}
          />
          {error("mobileNumber") && (
            <FormHelperText error>{formik.errors.mobileNumber}</FormHelperText>
          )}
        </div>
      </div>
    ),

    // A freelance company must state its revenue to submit its profile; a client need not.
    annualRevenue: (
      <div className={cell}>
        <label htmlFor="companyAnnualRevenue" className="block text-sm font-sm mb-1 text-[#505050]">
          {isFreelanceCompany ? required("Annual Revenue") : "Annual Revenue"}
        </label>
        <FormControl fullWidth error={Boolean(error("companyAnnualRevenue"))}>
          <Select
            id="companyAnnualRevenue"
            name="companyAnnualRevenue"
            value={formik.values.companyAnnualRevenue}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            displayEmpty
            disabled={disabled}
            className={`rounded-md h-[42px] px-3 text-sm ${
              disabled ? "bg-gray-100 text-gray-500" : "bg-white text-black"
            }`}
          >
            <MenuItem value="1" className="text-[#525354] text-sm">
              Under $1 Million
            </MenuItem>
            <MenuItem value="5-10" className="text-[#525354] text-sm">
              $5 - $10 Million
            </MenuItem>
            <MenuItem value="11" className="text-[#525354] text-sm">
              Above $10 Million
            </MenuItem>
          </Select>
          {error("companyAnnualRevenue") && (
            <FormHelperText>{formik.errors.companyAnnualRevenue}</FormHelperText>
          )}
        </FormControl>
      </div>
    ),

    logo: (
      <div className="col-span-2 flex flex-col">
        <label htmlFor="logoInput" className="block text-sm font-sm mb-1 text-[#505050]">
          Company Logo
        </label>
        <div
          className={`min-h-[160px] flex flex-col items-center justify-center w-full border-2 border-dashed rounded-md text-center p-4 transition ${
            disabled
              ? "cursor-not-allowed bg-gray-200 border-gray-400 opacity-50 pointer-events-none"
              : "cursor-pointer bg-white hover:border-primary border-gray-300"
          }`}
          onClick={() => {
            if (disabled) return;
            document.getElementById("logoInput").click();
          }}
        >
          {formik.values?.logo ? (
            <div className="flex flex-col items-center gap-2">
              <img
                src={
                  formik.values.logo.url
                    ? formik.values.logo.url
                    : URL.createObjectURL(formik.values.logo)
                }
                alt="Company Logo"
                className="max-h-32 object-contain rounded"
              />
              <span className="text-sm text-gray-600 mt-2">
                {formik.values.logo.fileName || formik.values.logo.name}
              </span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <i className="fa-solid fa-paperclip text-primary text-2xl"></i>
              <span className="text-sm text-gray-500">Click or drop file here</span>
              <span className="text-xs text-gray-400">Must be a JPG or PNG file</span>
            </div>
          )}

          <input
            type="file"
            id="logoInput"
            name="logo"
            accept="image/png, image/jpeg, image/webp"
            onChange={(event) => {
              const picked = event.target.files[0];
              if (!picked) return;
              if (!["image/jpeg", "image/png", "image/webp"].includes(picked.type)) {
                formik.setFieldError("logo", "Only JPG, PNG, or WEBP files are allowed");
                return;
              }
              formik.setFieldValue("logo", picked);
            }}
            onBlur={formik.handleBlur}
            className="hidden"
            disabled={disabled}
          />
        </div>
        {error("logo") && <p className="text-red-500 text-xs mt-1">{formik.errors.logo}</p>}
      </div>
    ),

    registrationNumber: text("registrationNumber", required("Registration Number")),
    vatNumber: text("vatNumber", "VAT/GST Registration", { optional: true }),
    registrationCertificate: file("registrationCertificate", required("Registration Certificate")),

    searchAddress: (
      <div className={cell}>
        <SearchLocation
          isDefaultField={true}
          label="Search Address"
          onPlaceSelected={handlePlaceSelected}
          disabled={disabled}
        />
      </div>
    ),
    address: text("address", required("Address")),
    city: text("city", "City"),
    state: text("state", "State"),
    zipCode: text("zipCode", "Zip Code"),
    country: text("country", "Country"),

    publicLiability: file("publicLiability", "Public Liability / Public Indemnity", {
      optional: true,
    }),
    workerCompensation: file("workerCompensation", "Worker Compensation", { optional: true }),
  };
};

export default companyFormFields;
